const es = {
  header: {
    badge: "Guía Oficial de BotMix",
    title: "Centro de Supervisión y Rendimiento — Monitoreo de Bots",
    summary:
      "El módulo de Monitoreo de Bots es el panel de control analítico para supervisar en tiempo real la efectividad, el volumen de interacciones y la tasa de resolución de todos los flujos automatizados y chatbots desplegados en tus líneas telefónicas. Desde aquí puedes auditar el desempeño de cada palabra clave, medir cuántas conversaciones fueron atendidas con éxito de manera autónoma y detectar de inmediato aquellos clientes que no recibieron respuesta o que requirieron transferencia a un agente humano.",
  },
  sections: [
    {
      id: "filtros",
      title: "1. Filtros de Selección y Rango Temporal",
      description:
        "Herramientas para segmentar cronológicamente las estadísticas de los bots.",
      items: [
        {
          id: "rango_fechas",
          title: "Fecha Inicial y Fecha Final",
          definition:
            "Permite acotar el periodo exacto de auditoría para evaluar el rendimiento de tus flujos durante campañas comerciales, fines de semana, turnos especiales o cierres mensuales.",
          importance:
            "Por defecto, el sistema consulta el día actual para ofrecer una fotografía en vivo de la operación en tiempo real.",
        },
        {
          id: "boton_filtrar",
          title: "Botón Filtrar",
          definition:
            "Ejecuta la consulta contra la base de datos de auditoría de FlowBuilder y actualiza instantáneamente tanto las tarjetas de resumen superior como la tabla de flujos sin necesidad de recargar el navegador.",
        },
      ],
    },
    {
      id: "indicadores_kpi",
      title: "2. Indicadores de Rendimiento General (Cuádruplas KPI)",
      description:
        "Métricas consolidadas del desempeño global de todas las automatizaciones activas.",
      items: [
        {
          id: "mensajes_entrantes",
          title: "Mensajes Entrantes",
          definition:
            "Total acumulado de nuevas conversaciones y contactos entrantes que interactuaron con algún canal de la empresa durante el periodo seleccionado.",
          importance:
            "Representa el volumen de demanda orgánica y el caudal de prospectos que ingresan a tus embudos comerciales y de soporte.",
        },
        {
          id: "respondidos",
          title: "Respondidos por el Bot",
          definition:
            "Cantidad de conversaciones en las cuales el chatbot o flujo automatizado se ejecutó correctamente y envió respuestas automáticas al usuario.",
          importance:
            "Demuestra la capacidad de atención inmediata 24/7 sin intervención humana.",
        },
        {
          id: "no_respondidos",
          title: "No Respondidos (Falla o Espera)",
          definition:
            "Conversaciones entrantes donde el bot no emitió una respuesta (por ejemplo, porque la palabra clave no coincidió con ningún flujo configurado, el usuario no continuó el árbol de decisiones o la sesión expiró).",
          alert:
            "Un valor alto en No Respondidos sugiere revisar las palabras clave del menú principal o activar un flujo por defecto (Fallback) para capturar a estos clientes.",
        },
        {
          id: "tasa_respuesta",
          title: "Tasa de Respuesta Global (%)",
          definition:
            "Porcentaje de efectividad resolutiva calculado como la relación entre los mensajes respondidos frente al total de mensajes entrantes.",
          importance:
            "Objetivo recomendado: Mantener la tasa por encima del 90% para garantizar una experiencia de autoservicio óptima.",
        },
      ],
    },
    {
      id: "tabla_conexiones",
      title: "3. Tabla de Conexiones y Rendimiento por Flujo",
      description:
        "Desglose detallado por línea telefónica, canal omnicanal y palabra clave disparadora.",
      items: [
        {
          id: "col_empresa",
          title: "Empresa / Cliente",
          definition:
            "Nombre de la organización propietaria de la línea telefónica (columna visible exclusivamente para usuarios con rol SuperAdmin).",
        },
        {
          id: "col_conexion",
          title: "Canal / Conexión",
          definition:
            "Identificación del número de teléfono o canal omnicanal (WhatsApp QR, API Oficial de Meta, Telegram o Instagram) donde opera el flujo.",
        },
        {
          id: "col_flujo",
          title: "Flujo / Palabra Clave",
          definition:
            "Nombre del flujo interactivo diseñado en FlowBuilder junto con la frase o palabra clave específica que dispara la secuencia de mensajes.",
        },
        {
          id: "col_mensajes",
          title: "Métricas Individuales (Entrantes / Respondidos / No Respondidos)",
          definition:
            "Valores cuantitativos específicos asignados a ese flujo en particular para evaluar individualmente qué árbol de decisiones tiene mayor tráfico y cuál presenta cuellos de botella.",
        },
        {
          id: "col_tasa_individual",
          title: "Semáforo de Efectividad (%)",
          definition:
            "Badge cromático que califica visualmente la tasa de resolución: Verde para efectividad superior al 90%, Naranja para rangos entre 70% y 90%, y Rojo para tasas inferiores al 70%.",
        },
      ],
    },
    {
      id: "faq",
      title: "4. Preguntas Frecuentes y Buenas Prácticas (FAQ)",
      description:
        "Recomendaciones clave para maximizar la automatización en BotMix.",
      items: [
        {
          id: "faq_coincidencia",
          title: "¿Qué ocurre si el cliente escribe con faltas de ortografía o sin tildes?",
          definition:
            "El motor de FlowBuilder cuenta con normalización de texto que detecta palabras clave ignorando mayúsculas, minúsculas y acentos para maximizar las coincidencias exitosas.",
        },
        {
          id: "faq_transferencia",
          title: "¿Un mensaje transferido a un asesor cuenta como respondido?",
          definition:
            "Sí. Si el flujo guio al cliente a través de opciones y luego lo derivó ordenadamente a una cola de agentes humanos, la interacción del bot se computa como respondida satisfactoriamente.",
        },
        {
          id: "faq_nuevas_conexiones",
          title: "¿Las nuevas conexiones se integran automáticamente en este monitoreo?",
          definition:
            "Sí. Cualquier nueva conexión que vincules en Conexiones o nuevo flujo que diseñes en FlowBuilder se incorporará de inmediato a las estadísticas de este reporte.",
        },
      ],
    },
  ],
};
const en = {
  "header": {
    "badge": "BotMix Official Guide",
    "title": "Supervision & Performance Center — Bot Monitoring",
    "summary": "The Bot Monitoring module is the analytical control panel to monitor in real time the effectiveness, interaction volume, and resolution rate of all automated flows and chatbots deployed on your phone lines. Audit keyword performance and measure autonomous resolution."
  },
  "sections": [
    {
      "id": "filtros",
      "title": "1. Selection Filters and Time Range",
      "description": "Tools to segment bot performance statistics chronologically.",
      "items": [
        {
          "id": "rango_fechas",
          "title": "Start and End Date",
          "definition": "Defines the exact audit timeframe to evaluate chatbots during campaigns, shifts, or closings."
        },
        {
          "id": "boton_filtrar",
          "title": "Filter Button",
          "definition": "Recalculates interaction counters and resolution charts immediately."
        }
      ]
    },
    {
      "id": "metricas_clave",
      "title": "2. Key Bot Performance Metrics",
      "description": "Quantitative KPIs of automated operations.",
      "items": [
        {
          "id": "total_interacciones",
          "title": "Total Interactions",
          "definition": "Number of sessions triggered by chatbots."
        },
        {
          "id": "tasa_resolucion",
          "title": "Autonomous Resolution Rate",
          "definition": "Percentage of conversations solved without human agent intervention."
        },
        {
          "id": "transferencias_agente",
          "title": "Transfers to Agent",
          "definition": "Conversations where customers requested or required human support."
        }
      ]
    }
  ]
};
const pt = {
  "header": {
    "badge": "Guia Oficial da BotMix",
    "title": "Centro de Supervisão e Desempenho — Monitoramento de Bots",
    "summary": "O módulo de Monitoramento de Bots é o painel de controle analítico para acompanhar em tempo real a eficácia, o volume de interações e a taxa de resolução dos fluxos automatizados e chatbots. Audite palavras-chave e meça a resolução autônoma."
  },
  "sections": [
    {
      "id": "filtros",
      "title": "1. Filtros e Período de Análise",
      "description": "Segmentação cronológica do desempenho dos robôs.",
      "items": [
        {
          "id": "rango_fechas",
          "title": "Data Inicial e Final",
          "definition": "Delimita o período exato para avaliar os fluxos."
        },
        {
          "id": "boton_filtrar",
          "title": "Botão Filtrar",
          "definition": "Atualiza instantaneamente os indicadores e gráficos."
        }
      ]
    },
    {
      "id": "metricas_clave",
      "title": "2. Métricas Principais de Bots",
      "description": "Indicadores quantitativos de automação.",
      "items": [
        {
          "id": "total_interacciones",
          "title": "Total de Interações",
          "definition": "Volume de conversas acionadas por robôs."
        },
        {
          "id": "tasa_resolucion",
          "title": "Taxa de Resolução Autônoma",
          "definition": "Percentual de tickets resolvidos sem atendente humano."
        },
        {
          "id": "transferencias_agente",
          "title": "Transferências para Atendentes",
          "definition": "Casos em que o cliente precisou de atendimento humano."
        }
      ]
    }
  ]
};
const fr = {
  "header": {
    "badge": "Guide Officiel BotMix",
    "title": "Supervision et Performance — Surveillance des Bots",
    "summary": "Le module de surveillance des bots est le tableau de bord analytique pour superviser en temps réel l'efficacité, le volume et le taux de résolution de tous vos scénarios automatisés et chatbots. Mesurez la résolution autonome et les demandes de transfert."
  },
  "sections": [
    {
      "id": "filtros",
      "title": "1. Filtres et Période d'Analyse",
      "description": "Segmentation temporelle des statistiques des bots.",
      "items": [
        {
          "id": "rango_fechas",
          "title": "Date Début et Date Fin",
          "definition": "Définit la période exacte d'évaluation."
        },
        {
          "id": "boton_filtrar",
          "title": "Bouton Filtrer",
          "definition": "Actualise les indicateurs et courbes d'activité."
        }
      ]
    },
    {
      "id": "metricas_clave",
      "title": "2. Métriques Clés des Bots",
      "description": "Indicateurs quantitatifs d'automatisation.",
      "items": [
        {
          "id": "total_interacciones",
          "title": "Interactions Totales",
          "definition": "Nombre de sessions traitées par les chatbots."
        },
        {
          "id": "tasa_resolucion",
          "title": "Taux de Résolution Autonome",
          "definition": "Pourcentage de requêtes résolues sans agent humain."
        },
        {
          "id": "transferencias_agente",
          "title": "Transferts vers Conseillers",
          "definition": "Dossiers ayant nécessité un relais humain."
        }
      ]
    }
  ]
};
const ar = {
  "header": {
    "badge": "دليل BotMix الرسمي",
    "title": "مركز الإشراف والأداء — مراقبة البوتات",
    "summary": "وحدة مراقبة البوتات هي لوحة التحكم التحليلية لمتابعة فعالية وحجم التفاعلات ومعدل الحل لجميع المسارات الآلية وروبوتات الدردشة المنشورة على أرقامك. راقب أداء الكلمات المفتاحية ومعدل الحل الذاتي."
  },
  "sections": [
    {
      "id": "filtros",
      "title": "1. فلاتر التحديد والنطاق الزمني",
      "description": "أدوات لتقسيم إحصائيات البوتات زمنيًا.",
      "items": [
        {
          "id": "rango_fechas",
          "title": "تاريخ البدء والانتهاء",
          "definition": "تحديد الفترة الزمنية الدقيقة للتدقيق."
        },
        {
          "id": "boton_filtrar",
          "title": "زر التصفية",
          "definition": "إعادة احتساب مؤشرات التفاعل ومعدل الحل فورًا."
        }
      ]
    },
    {
      "id": "metricas_clave",
      "title": "2. المقاييس الأساسية للبوتات",
      "description": "مؤشرات الأداء الكمية للعمليات الآلية.",
      "items": [
        {
          "id": "total_interacciones",
          "title": "إجمالي التفاعلات",
          "definition": "عدد الجلسات التي باشرها البوت."
        },
        {
          "id": "tasa_resolucion",
          "title": "معدل الحل الذاتي",
          "definition": "نسبة المحادثات التي تمت تسويتها دون تدخل بشري."
        },
        {
          "id": "transferencias_agente",
          "title": "التحويل للوكلاء",
          "definition": "المحادثات التي طلبت أو تطلبت تدخل وكيل بشري."
        }
      ]
    }
  ]
};

export const BOT_MONITOR_HELP_DATA = { es, en, pt, fr, ar };
